import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ExternalLink, Plus, ArrowLeft, ChevronRight, ChevronDown,
  CheckCircle, EyeOff, RotateCcw
} from 'lucide-react'
import { sentryApi } from '../services/api'
import { showError } from '../utils/showError'
import { formatTimeAgo } from '../utils/formatters'
import { levelColors, projectColors, statusColors } from '../constants/sentry'

import {
  PageHeader,
  LoadingSpinner,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Button,
} from '../components/common'

const SentryIssueDetail = () => {
  const { issueId } = useParams()
  const navigate = useNavigate()
  const [issue, setIssue] = useState(null)
  const [loading, setLoading] = useState(true)
  const [expandedFrames, setExpandedFrames] = useState({})
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [events, setEvents] = useState([])
  const [eventsExpanded, setEventsExpanded] = useState(false)
  const [eventsLoading, setEventsLoading] = useState(false)

  useEffect(() => {
    loadIssue()
  }, [issueId])

  const loadIssue = async () => {
    try {
      setLoading(true)
      const response = await sentryApi.getIssueDetail(issueId)
      setIssue(response.data)

      // Expand in-app frames by default
      if (response.data?.latestEvent?.exception?.frames) {
        const expanded = {}
        response.data.latestEvent.exception.frames.forEach((frame, idx) => {
          if (frame.inApp) expanded[idx] = true
        })
        setExpandedFrames(expanded)
      }
    } catch (error) {
      console.error('Error loading Sentry issue:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleEvents = async () => {
    if (!eventsExpanded && events.length === 0) {
      try {
        setEventsLoading(true)
        const response = await sentryApi.getIssueEvents(issueId)
        setEvents(response.data || [])
      } catch (error) {
        console.error('Error loading events:', error)
      } finally {
        setEventsLoading(false)
      }
    }
    setEventsExpanded(prev => !prev)
  }

  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdatingStatus(true)
      await sentryApi.updateStatus(issue.issueId, newStatus)
      setIssue(prev => ({ ...prev, status: newStatus }))
    } catch (error) {
      console.error('Error updating status:', error)
      showError(error)
    } finally {
      setUpdatingStatus(false)
    }
  }

  const toggleFrame = (idx) => {
    setExpandedFrames(prev => ({ ...prev, [idx]: !prev[idx] }))
  }

  if (loading) return <LoadingSpinner />
  if (!issue) return <div className="text-center py-12 text-gray-500">Issue not found</div>

  const createTaskUrl = `/tasks/create?sentryIssueId=${issue.issueId}&sentryIssueUrl=${encodeURIComponent(issue.permalink || '')}&title=${encodeURIComponent(issue.title || '')}`

  // Extract context info from tags
  const getTagValue = (key) => {
    const tag = issue.tags?.find(t => t.key === key)
    return tag?.topValues?.[0]?.value || null
  }

  const contextInfo = {
    browser: getTagValue('browser'),
    os: getTagValue('os'),
    device: getTagValue('device'),
    runtime: getTagValue('runtime'),
    environment: getTagValue('environment'),
    server: getTagValue('server_name'),
  }

  const contextTags = ['browser', 'os', 'device', 'runtime', 'environment', 'server_name']
  const displayTags = issue.tags?.filter(t => !contextTags.includes(t.key)) || []

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div>
            <span className="text-sm font-mono text-gray-400 font-normal">{issue.shortId}</span>
            <span className="mx-2 text-gray-300">|</span>
            <span>Sentry Issue</span>
          </div>
        }
        action={
          <Button variant="secondary" onClick={() => navigate('/sentry-issues')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ─── Left Column (2/3) ─── */}
        <div className="lg:col-span-2 space-y-6">

          {/* Error Card */}
          <Card>
            <CardBody>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant={levelColors[issue.level] || 'default'}>{issue.level}</Badge>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${projectColors[issue.projectName] || 'bg-gray-100 text-gray-700'}`}>
                  {issue.projectName}
                </span>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">{issue.title}</h2>
              {issue.culprit && (
                <p className="text-sm text-gray-500 font-mono mb-3">{issue.culprit}</p>
              )}
              {issue.exceptionValue && (
                <div className="bg-gray-900 rounded-lg p-4 mt-3">
                  <p className="text-sm font-mono text-red-400">{issue.exceptionType}</p>
                  <p className="text-sm font-mono text-gray-300 mt-1 whitespace-pre-wrap break-words">{issue.exceptionValue}</p>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Stacktrace Card */}
          {issue.latestEvent?.exception?.frames?.length > 0 && (
            <Card>
              <CardHeader title={`Stack Trace (${issue.latestEvent.exception.frames.length} frames)`} />
              <CardBody className="!p-0">
                <div className="divide-y divide-gray-100">
                  {issue.latestEvent.exception.frames.map((frame, idx) => (
                    <div key={idx} className={frame.inApp ? 'bg-primary-50/30' : 'bg-gray-50/50'}>
                      {/* Frame Header */}
                      <button
                        onClick={() => toggleFrame(idx)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-black/[0.02] transition-colors"
                      >
                        <ChevronRight
                          size={14}
                          className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${expandedFrames[idx] ? 'rotate-90' : ''}`}
                        />
                        <span className="text-xs font-mono text-gray-500 truncate flex-1">
                          <span className="text-blue-600">{frame.fileName || frame.module || '(unknown)'}</span>
                          {frame.lineNumber && <span className="text-gray-400">:{frame.lineNumber}</span>}
                          {frame.columnNumber && <span className="text-gray-400">:{frame.columnNumber}</span>}
                          {frame.function && (
                            <span className="text-gray-500"> in <span className="text-purple-600">{frame.function}</span></span>
                          )}
                        </span>
                        {frame.inApp && (
                          <span className="flex-shrink-0 text-[0.6rem] font-semibold uppercase tracking-wider bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded">
                            app
                          </span>
                        )}
                      </button>

                      {/* Frame Context (expanded) */}
                      {expandedFrames[idx] && frame.context && frame.context.length > 0 && (
                        <div className="border-t border-gray-100 bg-gray-900 overflow-x-auto">
                          <table className="w-full text-xs font-mono">
                            <tbody>
                              {frame.context.map((line, lIdx) => {
                                const lineNo = line[0]
                                const code = line[1]
                                const isCurrentLine = lineNo === frame.lineNumber
                                return (
                                  <tr
                                    key={lIdx}
                                    className={isCurrentLine ? 'bg-red-900/40' : ''}
                                  >
                                    <td className={`px-3 py-0.5 text-right select-none w-12 ${isCurrentLine ? 'text-red-300' : 'text-gray-600'}`}>
                                      {lineNo}
                                    </td>
                                    <td className={`px-3 py-0.5 whitespace-pre ${isCurrentLine ? 'text-red-200' : 'text-gray-300'}`}>
                                      {code}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Events Card */}
          <Card>
            <button
              onClick={toggleEvents}
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-sm font-semibold text-gray-900">
                Events {issue.eventCount ? `(${issue.eventCount.toLocaleString()})` : ''}
              </h3>
              <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform duration-200 ${eventsExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            {eventsExpanded && (
              <CardBody className="!pt-0">
                {eventsLoading ? (
                  <div className="flex justify-center py-6">
                    <LoadingSpinner />
                  </div>
                ) : events.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No events found</p>
                ) : (
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-gray-500 font-medium">Time</th>
                          <th className="px-3 py-2 text-left text-gray-500 font-medium">Event ID</th>
                          <th className="px-3 py-2 text-left text-gray-500 font-medium">Environment</th>
                          <th className="px-3 py-2 text-left text-gray-500 font-medium">Release</th>
                          <th className="px-3 py-2 text-left text-gray-500 font-medium">Platform</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {events.map((evt) => (
                          <tr key={evt.eventId} className="hover:bg-gray-50">
                            <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap font-mono">
                              {formatTimeAgo(evt.dateCreated)}
                            </td>
                            <td className="px-3 py-1.5 text-gray-700 font-mono">
                              {evt.eventId?.substring(0, 12)}...
                            </td>
                            <td className="px-3 py-1.5">
                              {evt.environment ? (
                                <Badge variant="default" size="small">{evt.environment}</Badge>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-1.5 text-gray-700 font-mono text-xs truncate max-w-[200px]">
                              {evt.release || '-'}
                            </td>
                            <td className="px-3 py-1.5 text-gray-600">
                              {evt.platform || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            )}
          </Card>

          {/* Breadcrumbs Card */}
          {issue.latestEvent?.breadcrumbs?.length > 0 && (
            <Card>
              <CardHeader title={`Breadcrumbs (${issue.latestEvent.breadcrumbs.length})`} />
              <CardBody className="!p-0">
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Time</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Category</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Message</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Level</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {issue.latestEvent.breadcrumbs.slice(-50).map((bc, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap font-mono">
                            {bc.timestamp ? new Date(bc.timestamp).toLocaleTimeString('en-US', { hour12: false }) : '-'}
                          </td>
                          <td className="px-3 py-1.5 text-gray-700">{bc.category || '-'}</td>
                          <td className="px-3 py-1.5 text-gray-900 max-w-md truncate">{bc.message || '-'}</td>
                          <td className="px-3 py-1.5">
                            {bc.level && (
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                bc.level === 'error' ? 'bg-red-100 text-red-700' :
                                bc.level === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {bc.level}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* ─── Right Column (1/3) ─── */}
        <div className="space-y-6">

          {/* Issue Info Card */}
          <Card>
            <CardHeader title="Issue Info" />
            <CardBody>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Status</dt>
                  <dd>
                    <Badge variant={statusColors[issue.status] || 'default'}>
                      {issue.status}
                    </Badge>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">First Seen</dt>
                  <dd className="text-gray-900">{formatTimeAgo(issue.firstSeen)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Last Seen</dt>
                  <dd className="text-gray-900">{formatTimeAgo(issue.lastSeen)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Events</dt>
                  <dd className="text-gray-900 font-medium">{issue.eventCount?.toLocaleString()}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Users</dt>
                  <dd className="text-gray-900 font-medium">{issue.userCount?.toLocaleString()}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Project</dt>
                  <dd>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${projectColors[issue.projectName] || 'bg-gray-100 text-gray-700'}`}>
                      {issue.projectName}
                    </span>
                  </dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* Tags Card */}
          {displayTags.length > 0 && (
            <Card>
              <CardHeader title="Tags" />
              <CardBody>
                <div className="space-y-2">
                  {displayTags.map((tag) => (
                    <div key={tag.key} className="flex justify-between text-sm">
                      <span className="text-gray-500 truncate mr-2">{tag.name || tag.key}</span>
                      <span className="text-gray-900 truncate text-right font-mono text-xs">
                        {tag.topValues?.[0]?.value || '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Context Card */}
          {Object.values(contextInfo).some(Boolean) && (
            <Card>
              <CardHeader title="Context" />
              <CardBody>
                <dl className="space-y-2 text-sm">
                  {contextInfo.browser && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Browser</dt>
                      <dd className="text-gray-900 text-right truncate ml-2">{contextInfo.browser}</dd>
                    </div>
                  )}
                  {contextInfo.os && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">OS</dt>
                      <dd className="text-gray-900 text-right truncate ml-2">{contextInfo.os}</dd>
                    </div>
                  )}
                  {contextInfo.device && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Device</dt>
                      <dd className="text-gray-900 text-right truncate ml-2">{contextInfo.device}</dd>
                    </div>
                  )}
                  {contextInfo.runtime && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Runtime</dt>
                      <dd className="text-gray-900 text-right truncate ml-2">{contextInfo.runtime}</dd>
                    </div>
                  )}
                  {contextInfo.environment && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Environment</dt>
                      <dd className="text-gray-900 text-right truncate ml-2">{contextInfo.environment}</dd>
                    </div>
                  )}
                  {contextInfo.server && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Server</dt>
                      <dd className="text-gray-900 text-right truncate ml-2">{contextInfo.server}</dd>
                    </div>
                  )}
                </dl>
              </CardBody>
            </Card>
          )}

          {/* Actions Card */}
          <Card>
            <CardHeader title="Actions" />
            <CardBody>
              <div className="space-y-2">
                {issue.status !== 'resolved' && (
                  <button
                    onClick={() => handleStatusUpdate('resolved')}
                    disabled={updatingStatus}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <CheckCircle size={16} />
                    Resolve
                  </button>
                )}
                {issue.status !== 'ignored' && (
                  <button
                    onClick={() => handleStatusUpdate('ignored')}
                    disabled={updatingStatus}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <EyeOff size={16} />
                    Ignore
                  </button>
                )}
                {issue.status !== 'unresolved' && (
                  <button
                    onClick={() => handleStatusUpdate('unresolved')}
                    disabled={updatingStatus}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <RotateCcw size={16} />
                    Unresolve
                  </button>
                )}
                {issue.permalink && (
                  <a
                    href={issue.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <ExternalLink size={16} />
                    View on Sentry
                  </a>
                )}
                <button
                  onClick={() => navigate(createTaskUrl)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                >
                  <Plus size={16} />
                  Create Task
                </button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default SentryIssueDetail
